class CntrlPlusCartFlow {
  constructor() {
    const { starterKitProductId, refillProductId } = document.documentElement.dataset;

    this.starterKitProductId = starterKitProductId
      ? parseInt(starterKitProductId)
      : null;
    this.refillProductId = refillProductId ? parseInt(refillProductId) : null;
    this.refillsBySize = null;
    this.routes = window.theme.routes || {};
    this.paths = {
      base: `${this.routes.cart.base || "/cart"}.js`,
      change: `${this.routes.cart.change || "/cart/change"}.js`,
    };

    if (this.starterKitProductId) {
      this.initStarterKitEnforcement();
    }

    this.initReplacementRefillCount();
    this.initRefillFormGuard();
    this.initRefillLoginRedirect();
  }

  initReplacementRefillCount() {
    this.refillCountEl = document.querySelector("[data-refill-count-text]");
    if (!this.refillCountEl) return;

    const serializedCounts = this.refillCountEl.dataset.refillCountsRemaining;
    const serializedTotals = this.refillCountEl.dataset.refillCountsTotal;
    if (!serializedCounts || serializedCounts === "null") {
      this.refillCountEl.textContent = "Sign in to see remaining refills.";
      return;
    }

    try {
      this.refillsBySize = this.parseRefillCounts(serializedCounts);
      if (!this.refillsBySize) {
        this.refillCountEl.textContent = "Unable to load refill count right now.";
        return;
      }

      this.totalRefillsBySize = this.parseRefillCounts(serializedTotals || "") || {
        small: this.refillsBySize.small,
        medium: this.refillsBySize.medium,
        large: this.refillsBySize.large,
        total: this.refillsBySize.total,
      };

      this.bindVariantRefillCountUpdates();
      this.updateRefillCountText();
    } catch (_err) {
      this.refillCountEl.textContent = "Unable to load refill count right now.";
    }
  }

  parseRefillCounts(serializedCounts) {
    let parsed = serializedCounts;

    // Shopify/Liquid can produce either an object JSON string or a double-encoded JSON string.
    for (let i = 0; i < 2; i += 1) {
      if (typeof parsed !== "string") break;
      parsed = JSON.parse(parsed);
    }

    if (!parsed || typeof parsed !== "object") return null;

    return {
      small: Number(parsed.small) || 0,
      medium: Number(parsed.medium) || 0,
      large: Number(parsed.large) || 0,
      total: Number(parsed.total) || 0,
    };
  }

  bindVariantRefillCountUpdates() {
    const buyButtonsBlock = document.querySelector("product-block-buy-buttons");
    if (buyButtonsBlock) {
      buyButtonsBlock.addEventListener("product:variant:changed", () => {
        this.updateRefillCountText();
      });
    }

    const variantSelector = document.querySelector("product-block-variant-selector");
    if (variantSelector) {
      variantSelector.addEventListener("change", () => {
        this.updateRefillCountText();
      });
    }
  }

  normalizeSize(sizeLabel) {
    const value = (sizeLabel || "").toLowerCase();
    if (value === "s" || /\bsmall\b/.test(value)) return "small";
    if (value === "m" || /\bmedium\b/.test(value)) return "medium";
    if (value === "l" || /\blarge\b/.test(value)) return "large";
    if (value.includes("small")) return "small";
    if (value.includes("medium")) return "medium";
    if (value.includes("large")) return "large";
    return "";
  }

  updateQuantityInputLimit(maxQuantity) {
    const quantityInput = document.querySelector("[js-quantity-input]");
    if (!quantityInput) return;

    const minusButton = document.querySelector("[js-quantity-minus]");
    const plusButton = document.querySelector("[js-quantity-plus]");
    const normalizedMax = Math.max(0, Number(maxQuantity) || 0);

    quantityInput.setAttribute("max", String(normalizedMax));

    if (normalizedMax <= 0) {
      quantityInput.value = "0";
      quantityInput.setAttribute("disabled", "disabled");
      minusButton?.setAttribute("disabled", "disabled");
      plusButton?.setAttribute("disabled", "disabled");
      quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    quantityInput.removeAttribute("disabled");
    minusButton?.removeAttribute("disabled");
    plusButton?.removeAttribute("disabled");

    const currentQuantity = Number(quantityInput.value) || 1;
    if (currentQuantity > normalizedMax || currentQuantity <= 0) {
      quantityInput.value = String(Math.min(Math.max(1, currentQuantity), normalizedMax));
      quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  updateRefillButtonState(remainingCount) {
    const buttons = document.querySelectorAll('[js-add-to-cart-button]');
    if (!buttons.length) return;
    const hasRefills = remainingCount > 0;
    const newLabel = hasRefills
      ? (window.theme.strings?.cart?.addToCart || 'Add to cart')
      : 'Prescription Required';
    buttons.forEach((button) => {
      button.disabled = !hasRefills;
      button
        .querySelectorAll('.label-idle__inner, .label-default, .button-with-state__label--hidden')
        .forEach((el) => { el.textContent = newLabel; });
    });
  }

  getSelectedSizeKey() {
    const checkedVariantInput = document.querySelector("[js-variant-input][type='radio']:checked");
    if (checkedVariantInput) {
      const checkedLabelText = checkedVariantInput.closest("label")?.textContent?.trim() || "";
      const checkedSize = this.normalizeSize(checkedLabelText);
      if (checkedSize) return checkedSize;
    }

    const selectedLabelValue = document.querySelector("[js-variant-label-value]")?.textContent?.trim();
    const selectedLabelSize = this.normalizeSize(selectedLabelValue || "");
    if (selectedLabelSize) return selectedLabelSize;

    const selectedVariantInput = document.querySelector("[data-product-form] input[name='id']");
    const selectedVariantId = selectedVariantInput?.value;
    if (!selectedVariantId) return "";

    const optionInput = document.querySelector(
      `[js-variant-input][data-variant-id='${selectedVariantId}']`
    );

    if (!optionInput) return "";

    const optionLabel = optionInput.closest("label")?.textContent?.trim() || "";
    return this.normalizeSize(optionLabel);
  }

  updateRefillCountText() {
    if (!this.refillCountEl || !this.refillsBySize || !this.totalRefillsBySize) return;

    const sizeKey = this.getSelectedSizeKey();
    if (!sizeKey) {
      this.refillCountEl.textContent = "Select a size to see remaining refills.";
      return;
    }

    const remainingCount = Number(this.refillsBySize[sizeKey]) || 0;
    const totalCount = Number(this.totalRefillsBySize[sizeKey]) || remainingCount;
    const refillLabel = totalCount === 1 ? "Refill" : "Refills";

    this.updateQuantityInputLimit(remainingCount);
    this.updateRefillButtonState(remainingCount);
    this.refillCountEl.textContent = `${remainingCount} of ${totalCount} ${refillLabel} Available`;
  }

  initStarterKitEnforcement() {
    document.addEventListener("cart:changed", (evt) => {
      this.enforceStarterKitLimit(evt.detail.cartData);
      this.updateStarterKitButton(evt.detail.cartData);

      // Re-apply after button animation completes (~1.8s) to ensure
      // the state persists for all users regardless of login status
      setTimeout(() => {
        this.updateStarterKitButton(evt.detail.cartData);
      }, 2000);
    });

    document.addEventListener("cart:item-added", (evt) => {
      this.updateStarterKitButton(evt.detail.cartData);
    });
  }

  enforceStarterKitLimit(cartData) {
    const { items } = cartData;
    if (!items || !items.length) return;

    const starterKitItem = items.find(
      (item) => item.product_id === this.starterKitProductId
    );

    if (starterKitItem && starterKitItem.quantity > 1) {
      this.changeItemQuantity(starterKitItem.key, 1);
    }
  }

  updateStarterKitButton(cartData) {
    const buttonWrapper = document.querySelector("[data-starter-kit-button]");
    if (!buttonWrapper) return;

    const button = buttonWrapper.querySelector("[js-button-with-state]");
    if (!button) return;

    const items = cartData.items || [];
    const inCart = items.some((item) => item.product_id === this.starterKitProductId);

    const addToCartLabel = window.theme.strings?.cart?.addToCart || "Add to cart";
    const limitLabel = "Limit 1 per customer";
    const newLabel = inCart ? limitLabel : addToCartLabel;

    button.disabled = inCart;

    button.querySelectorAll(
      ".label-idle__inner, .label-default, .button-with-state__label--hidden"
    ).forEach((el) => {
      el.textContent = newLabel;
    });
  }

  changeItemQuantity(id, quantity) {
    const fetchData = {
      id: `${id}`,
      quantity,
    };

    return fetch(this.paths.change, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fetchData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 422) {
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: { errorMessage: data.description },
            })
          );
        } else {
          this._emitEvent(data);
        }
      });
  }

  initRefillFormGuard() {
    if (!this.refillProductId) return;

    const productForm = document.querySelector('[data-product-form]');
    if (!productForm) return;

    // Only active when the form belongs to the refill product
    const formProductHandle = productForm.dataset.productHandle;
    const refillVariantInput = document.querySelector(
      `[data-product-form] input[name='id']`
    );
    // Guard fires only when refillsBySize is populated (refill product page)
    productForm.addEventListener('submit', (event) => {
      if (!this.refillsBySize) return;

      const sizeKey = this.getSelectedSizeKey();
      if (!sizeKey) return;

      const remaining = Number(this.refillsBySize[sizeKey]) || 0;
      const quantityInput = productForm.querySelector('[js-quantity-input]');
      const quantity = Number(quantityInput?.value || 1);

      if (remaining <= 0 || quantity > remaining) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const errorEl = document.querySelector('[js-add-to-cart-error]');
        if (errorEl) {
          errorEl.textContent = remaining <= 0
            ? 'No refills remaining for this size.'
            : `You only have ${remaining} refill${remaining === 1 ? '' : 's'} remaining for this size.`;
          errorEl.scrollIntoView({ block: 'center' });
        }

        document.querySelector('product-block-buy-buttons')?.classList.add('has-error');
      }
    }, true);
  }

  initRefillLoginRedirect() {
    const wrapper = document.querySelector('[data-refill-login-redirect]');
    if (!wrapper) return;
    wrapper.addEventListener('click', (event) => {
      event.preventDefault();
      const returnTo = window.location.pathname + window.location.search;
      window.location.href = '/account/login?return_to=' + encodeURIComponent(returnTo);
    });
  }

  _emitEvent(cartData) {
    const eventOptions = {
      detail: { cartData },
      bubbles: true,
    };

    document.dispatchEvent(new CustomEvent("cart:item-changed", eventOptions));
    document.dispatchEvent(new CustomEvent("cart:changed", eventOptions));
  }
}

new CntrlPlusCartFlow();
